import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import uniqBy from 'lodash/uniqBy';
import React from 'react';
import { Column } from 'src/features/kanban/component/Column/types';

import { ChartBase } from '../ChartBase';
import { commonGridAxisValues, getTotalTasks, theme, unassignedUser } from '../utils';
import { CustomAxisLabelImage } from './CustomAxisLabelImage';

export const AssigneeWorkload = ({ data }: { data: Column[] }) => {
  const totalTasks = getTotalTasks(data);
  const allTaskAssignees = data.map((c) => c.items.map((i) => i.assignee).flat()).flat();
  const allTasks = data.map((c) => c.items.flat()).flat();
  const uniqueAssignees = uniqBy(allTaskAssignees, 'memberId');
  const assigneesWithTasks = uniqueAssignees.map((i) => ({
    ...i,
    tasks: {
      completed: allTasks.filter((task) =>
        task.assignee?.some((member) => member.memberId === i?.memberId && task.completed)
      ).length,
      incomplete: allTasks.filter((task) =>
        task.assignee?.some((member) => member.memberId === i?.memberId && !task.completed)
      ).length,
      total: allTasks.filter((task) =>
        task.assignee?.some((member) => member.memberId === i?.memberId)
      ).length,
    },
  }));

  const unassignedWithTasks = {
    ...unassignedUser,
    tasks: {
      total: allTasks.filter((task) => (task.assignee?.length || 0) < 1).length,
      completed: allTasks.filter((task) => (task.assignee?.length || 0) < 1 && task.completed)
        .length,
      incomplete: allTasks.filter((task) => (task.assignee?.length || 0) < 1 && !task.completed)
        .length,
    },
  };

  const aggregateData = [...assigneesWithTasks, unassignedWithTasks];

  const dataBar = aggregateData.map((member) => ({
    assignee: member.firstname,
    completed: member.tasks.completed.toString(),
    incomplete: member.tasks.incomplete.toString(),
    profile: member.avatar,
  }));

  const dataPie = aggregateData.map((member) => ({
    id: member.firstname,
    label: member.firstname,
    value: member.tasks.total,
    completed: member.tasks.completed,
    incomplete: member.tasks.incomplete,
  }));

  const TotalTaskCountLayer = ({ centerX, centerY }: { centerX: number; centerY: number }) => (
    <>
      <text
        x={centerX}
        y={centerY + 5}
        textAnchor="middle"
        fill="#333333"
        style={{
          fontSize: 50,
          fontWeight: 800,
        }}
      >
        {totalTasks}
      </text>
      <text
        x={centerX + 2}
        y={centerY + 30}
        textAnchor="middle"
        fill="#33333368"
        style={{
          fontSize: 16,
          fontWeight: 200,
        }}
      >
        Tasks
      </text>
    </>
  );

  const chartObject = [
    {
      type: 'bar',
      chart: (
        <ResponsiveBar
          data={dataBar as any}
          keys={['completed', 'incomplete']}
          indexBy="profile"
          margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
          padding={0.8}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={['#B0EEBB', '#FCCFCA']}
          borderRadius={5}
          axisTop={null}
          axisRight={null}
          axisLeft={{
            tickValues: commonGridAxisValues,
          }}
          enableLabel={false}
          gridYValues={commonGridAxisValues}
          labelSkipWidth={12}
          labelSkipHeight={12}
          axisBottom={{
            renderTick: (tick) => <CustomAxisLabelImage tick={tick} />,
          }}
          tooltip={({ data }) => (
            <div
              style={{
                padding: 8,
                background: '#ffffff',
                fontSize: '12px',
                boxShadow: '0px 2px 8px rgba(19, 23, 32, 0.16)',
                borderRadius: '5px',
              }}
            >
              <p className="font-medium">{data.assignee}</p>
              <p>Completed: {data.completed}</p>
              <p>Incomplete: {data.incomplete}</p>
            </div>
          )}
          role="application"
          ariaLabel="Tasks count by assigned to"
        />
      ),
    },
    {
      type: 'donut',
      chart: (
        <ResponsivePie
          data={dataPie}
          colors={theme}
          margin={{ top: 40, right: 80, bottom: 80, left: 0 }}
          innerRadius={0.85}
          padAngle={0.7}
          cornerRadius={20}
          activeOuterRadiusOffset={8}
          borderWidth={0}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 0.2]],
          }}
          layers={['arcLinkLabels', 'arcs', 'arcLabels', 'legends', TotalTaskCountLayer]}
          enableArcLinkLabels={false}
          arcLabelsSkipAngle={10}
          arcLabelsTextColor={{
            from: 'color',
            modifiers: [['brighter', 10]],
          }}
          arcLabelsComponent={({ datum, label, style }) => (
            <g
              transform={JSON.stringify(style.transform).slice(1, -1)}
              style={{ pointerEvents: 'none' }}
            >
              <rect
                fill="#ffffff"
                stroke={datum.color}
                strokeWidth={2}
                x={-20}
                y={-10}
                width={40}
                height={20}
                rx={10}
              />
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fill={datum.color}
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                }}
              >
                {label}
              </text>
            </g>
          )}
          tooltip={({ datum }) => (
            <div
              style={{
                padding: 8,
                background: '#ffffff',
                fontSize: '12px',
                boxShadow: '0px 2px 8px rgba(19, 23, 32, 0.16)',
                borderRadius: '5px',
                borderTop: `5px solid ${datum.color}`,
              }}
            >
              <p className="font-medium">{datum.data.label}</p>
              <p>Completed: {datum.data.completed}</p>
              <p>Incomplete: {datum.data.incomplete}</p>
            </div>
          )}
          legends={[
            {
              anchor: 'right',
              direction: 'column',
              justify: false,
              translateX: 40,
              translateY: 0,
              itemsSpacing: 10,
              itemWidth: 100,
              itemHeight: 18,
              itemTextColor: '#999',
              itemDirection: 'left-to-right',
              itemOpacity: 1,
              symbolSize: 16,
              symbolShape: 'circle',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemTextColor: '#000',
                  },
                },
              ],
            },
          ]}
        />
      ),
    },
  ];

  return <ChartBase data={chartObject} noData={totalTasks < 1} title="📈&nbsp;Assignee workload" />;
};
